import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { merge } from 'lodash-es';
import { FUSE_APP_CONFIG } from '@fuse/services/config/config.constants';
import { environment } from 'environments/environment';

type LayoutBrandingChanges = {
    theme?: string;
    logo?: string;
    watermark?: string;
    favicon?: string;
};

@Injectable({
    providedIn: 'root'
})
export class FuseConfigService {
    private _config: BehaviorSubject<any>;

    /**
     * Constructor
     */
    constructor(@Inject(FUSE_APP_CONFIG) config: any) {
        // Private
        this._config = new BehaviorSubject(config);

        const runtimeLayout = environment.layout || {};
        const storedConfig = this._getStoredAppConfig();
        const theme = localStorage.getItem('app-theme') || storedConfig.theme || runtimeLayout.theme || 'default';

        this.config = {
            "logo": storedConfig.logo || runtimeLayout.logo || "https://cloud.ashcorp.co/index.php/s/fX7kZWEkEysJrqb/download/logo-20260328151452-e9f33243.png",
            "theme": theme,
            "watermark": storedConfig.watermark || runtimeLayout.watermark || "https://cloud.ashcorp.co/index.php/s/Btp2mpragCTSniC/download/watermark-20260328151320-0bcf126c.png",
            "favicon": storedConfig.favicon || runtimeLayout.favicon || "https://cloud.ashcorp.co/index.php/s/MwMofkqidT9pYPZ/download/favicon-20260328151518-4af7f78e.png"
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for config
     */
    set config(value: any) {
        // Merge the new config over to the current config
        const config = merge({}, this._config.getValue(), value);
        localStorage.setItem('app-theme', config.theme);
        this._setFavicon(config.favicon);

        // Execute the observable
        this._config.next(config);
    }

    get config$(): Observable<any> {
        return this._config.asObservable();
    }

    get config(): any {
        return this._config.getValue();
    }

    /**
     * Getter pour le thème actuel
     */
    get theme(): string {
        return this._config.getValue().theme ?? "default";
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Resets the config to the default
     */
    reset(): void {
        // Set the config
        this._config.next(this.config);
    }

    previewConfig(value: any): void {
        this.applyTemporaryLayoutChanges(value);
    }

    applyTemporaryLayoutChanges(value: LayoutBrandingChanges): void {
        const config = merge({}, this._config.getValue(), value);
        this._setFavicon(config.favicon);
        this._config.next(config);
    }

    applyPermanentLayoutChanges(value: LayoutBrandingChanges): void {
        const config = merge({}, this._config.getValue(), value);
        const persistedConfig: LayoutBrandingChanges = {
            theme: config.theme,
            logo: config.logo,
            watermark: config.watermark,
            favicon: config.favicon
        };

        localStorage.setItem('app-theme', config.theme || 'default');
        localStorage.setItem('app-config', JSON.stringify(persistedConfig));
        this._setFavicon(config.favicon);
        this._config.next(config);
    }

    private _getStoredAppConfig(): any {
        try {
            return JSON.parse(localStorage.getItem('app-config') || '{}');
        } catch (error) {
            return {};
        }
    }

    private _setFavicon(url: string): void {
        if (!url || typeof document === 'undefined') {
            return;
        }

        const favicon = document.getElementById('app-favicon');
        if (favicon) {
            favicon.setAttribute('href', url);
        }
    }
}
